/**
 * Achievement Service
 * Handles achievement unlock logic and tracking
 */

import { ACHIEVEMENTS } from '../data/achievements';
import type { Achievement, UserAchievement, UserProgress, UserPathProgress } from '../types/academy';

const STORAGE_KEY = 'wealthhub_academy_achievements';

// Get user achievements from localStorage
export function getUserAchievements(): UserAchievement[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const achievements = JSON.parse(saved);
      // Convert string dates back to Date objects
      return achievements.map((a: any) => ({
        ...a,
        unlockedAt: new Date(a.unlockedAt)
      }));
    }
  } catch (error) {
    console.error('Error loading achievements:', error);
  }
  return [];
}

// Save user achievements to localStorage
export function saveUserAchievements(achievements: UserAchievement[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(achievements));
  } catch (error) {
    console.error('Error saving achievements:', error);
  }
}

// Check if user has unlocked an achievement
export function hasAchievement(achievementId: string, userAchievements: UserAchievement[]): boolean {
  return userAchievements.some(a => a.achievementId === achievementId);
}

// Unlock an achievement
export function unlockAchievement(achievementId: string): UserAchievement {
  const newAchievement: UserAchievement = {
    userId: 'default',
    achievementId,
    unlockedAt: new Date(),
    isNotified: false
  };

  const current = getUserAchievements();
  if (!hasAchievement(achievementId, current)) {
    current.push(newAchievement);
    saveUserAchievements(current);
  }

  return newAchievement;
}

// Mark achievement as notified
export function markAsNotified(achievementId: string): void {
  const current = getUserAchievements();
  const achievement = current.find(a => a.achievementId === achievementId);
  if (achievement) {
    achievement.isNotified = true;
    saveUserAchievements(current);
  }
}

// Check all achievements and unlock any that qualify
export function checkAchievements(
  userProgress: Record<string, UserProgress>,
  pathProgress: Record<string, UserPathProgress>
): UserAchievement[] {
  const currentAchievements = getUserAchievements();
  const newlyUnlocked: UserAchievement[] = [];

  // Calculate stats
  const completedCourses = Object.values(userProgress).filter(p => p.isCompleted).length;
  const totalWatchTime = Object.values(userProgress).reduce((sum, p) => sum + (p.totalDuration / 60), 0); // in minutes

  // Check each achievement
  for (const achievement of ACHIEVEMENTS) {
    // Skip if already unlocked
    if (hasAchievement(achievement.id, currentAchievements)) {
      continue;
    }

    let shouldUnlock = false;

    switch (achievement.condition.type) {
      case 'courses_completed':
        if (achievement.condition.target && completedCourses >= achievement.condition.target) {
          shouldUnlock = true;
        }
        break;

      case 'watch_time':
        if (achievement.condition.target && totalWatchTime >= achievement.condition.target) {
          shouldUnlock = true;
        }
        break;

      case 'path_completed':
        if (achievement.condition.pathId) {
          const path = pathProgress[achievement.condition.pathId];
          if (path && path.completionPercentage === 100) {
            shouldUnlock = true;
          }
        }
        break;

      case 'streak_days':
        // TODO: Implement streak tracking
        // For now, skip streak achievements
        break;

      case 'custom':
        // Handle custom conditions
        shouldUnlock = checkCustomCondition(achievement.condition.customCheck || '', userProgress, pathProgress);
        break;
    }

    if (shouldUnlock) {
      const newAchievement = unlockAchievement(achievement.id);
      newlyUnlocked.push(newAchievement);
    }
  }

  return newlyUnlocked;
}

// Check custom achievement conditions
function checkCustomCondition(
  checkType: string,
  userProgress: Record<string, UserProgress>,
  pathProgress: Record<string, UserPathProgress>
): boolean {
  switch (checkType) {
    case 'all_paths': {
      // Check if all 5 paths are completed
      const pathIds = ['beginner', 'trader', 'analyst', 'portfolio-manager', 'advanced'];
      return pathIds.every(id => pathProgress[id]?.completionPercentage === 100);
    }

    case 'all_courses': {
      // TODO: Compare completed courses with total available courses
      return false;
    }

    case 'early_morning': {
      // Check if any course was completed before 8 AM
      return Object.values(userProgress).some(p => {
        if (!p.completedAt) return false;
        const hour = new Date(p.completedAt).getHours();
        return hour < 8;
      });
    }

    case 'late_night': {
      // Check if any course was completed after midnight
      return Object.values(userProgress).some(p => {
        if (!p.completedAt) return false;
        const hour = new Date(p.completedAt).getHours();
        return hour >= 0 && hour < 6;
      });
    }

    case 'three_in_day': {
      // Check if 3 courses were completed on the same day
      const completedDates = Object.values(userProgress)
        .filter(p => p.completedAt)
        .map(p => new Date(p.completedAt!).toDateString());

      const dateCount: Record<string, number> = {};
      completedDates.forEach(date => {
        dateCount[date] = (dateCount[date] || 0) + 1;
      });

      return Object.values(dateCount).some(count => count >= 3);
    }

    case 'weekend_courses': {
      // Check if 5 courses were completed on weekends
      const weekendCompletions = Object.values(userProgress).filter(p => {
        if (!p.completedAt) return false;
        const day = new Date(p.completedAt).getDay();
        return day === 0 || day === 6; // Sunday or Saturday
      }).length;

      return weekendCompletions >= 5;
    }

    default:
      return false;
  }
}

// Get total points earned
export function getTotalPoints(userAchievements: UserAchievement[]): number {
  return userAchievements.reduce((sum, ua) => {
    const achievement = ACHIEVEMENTS.find(a => a.id === ua.achievementId);
    return sum + (achievement?.points || 0);
  }, 0);
}

// Get achievement progress for display
export function getAchievementProgress(
  achievement: Achievement,
  userProgress: Record<string, UserProgress>,
  pathProgress: Record<string, UserPathProgress>
): number {
  const completedCourses = Object.values(userProgress).filter(p => p.isCompleted).length;
  const totalWatchTime = Object.values(userProgress).reduce((sum, p) => sum + (p.totalDuration / 60), 0);

  switch (achievement.condition.type) {
    case 'courses_completed':
      if (!achievement.condition.target) return 0;
      return Math.min(100, (completedCourses / achievement.condition.target) * 100);

    case 'watch_time':
      if (!achievement.condition.target) return 0;
      return Math.min(100, (totalWatchTime / achievement.condition.target) * 100);

    case 'path_completed':
      if (!achievement.condition.pathId) return 0;
      return pathProgress[achievement.condition.pathId]?.completionPercentage || 0;

    default:
      return 0;
  }
}
